import React from "react";
import {
  Chart as ChartJS,
  BarElement,
  LinearScale,
  CategoryScale,
  Tooltip,
  Legend,
  ChartOptions,
} from "chart.js";
import { Bar } from "react-chartjs-2";

ChartJS.register(BarElement, LinearScale, CategoryScale, Tooltip, Legend);

interface HistogramChartProps {
  evictionData: Record<string, Record<string, number>>;
  xAxisLabel: string;
  title: string;
}

const HistogramChart: React.FC<HistogramChartProps> = ({ evictionData, xAxisLabel, title }) => {
  const xLabels = Object.keys(evictionData).sort();
  const reasonsSet = new Set<string>();

  xLabels.forEach((label) => {
    Object.keys(evictionData[label]).forEach((reason) => {
      reasonsSet.add(reason);
    });
  });

  const reasons = Array.from(reasonsSet).sort();

  const colorPalette = [
    "#A7C7E7", "#BFD3C1", "#89AEB2", "#D3DCE3", "#A5BFD6",
    "#C5D8E1", "#8FA7C7", "#E2E8F0", "#9DBAD5", "#B7C8DB",
    "#98AFC7", "#C0D3E0"
  ];

  const datasets = reasons.map((reason, index) => ({
    label: reason,
    data: xLabels.map((label) => evictionData[label][reason] || 0),
    backgroundColor: colorPalette[index % colorPalette.length],
    borderColor: "#fff",
    borderWidth: 1,
    borderRadius: 4,
  }));

  const data = {
    labels: xLabels,
    datasets,
  };

  const options: ChartOptions<"bar"> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      title: {
        display: false,
        text: title,
        font: {
          size: 12,
          weight: "bold"
        },
        color: '#333',
        padding: { bottom: 10 },
        align: 'center'
      },
      legend: {
        display: true,
        position: 'top',
        labels: {
          boxWidth: 12,
          padding: 10,
          font: {
            size: 10
          },
          usePointStyle: true,
          pointStyle: 'rect'
        }
      },
      tooltip: {
        backgroundColor: "#fff",
        borderColor: "#ddd",
        borderWidth: 1,
        padding: 8,
        bodyColor: "#333",
        titleColor: "#333",
        titleFont: {
          size: 10
        },
        bodyFont: {
          size: 10
        },
        callbacks: {
          label: function (context) {
            const value = context.raw as number;
            return `${context.dataset.label}: ${value.toLocaleString()}`;
          },
        },
      },
    },
    scales: {
      x: {
        stacked: true,
        grid: {
          display: false
        },
        ticks: {
          font: {
            size: 10,
          },
          color: "#555",
          maxRotation: 45,
          minRotation: 30,
          callback: function (value) {
            const label = this.getLabelForValue(value as number);
            return label.length > 7 ? label.substring(0, 7) + "…" : label;
          },
        },
        title: {
          display: false,
          text: xAxisLabel,
          font: {
            size: 10
          },
          padding: { top: 10 }
        },
      },
      y: {
        stacked: true,
        beginAtZero: true,
        grid: {
          color: "rgba(180, 180, 180, 0.1)"
        },
        ticks: {
          font: {
            size: 10
          },
          color: "#555",
          callback: function (value) {
            const numericValue = typeof value === 'string' ? parseFloat(value) : value;
            if (numericValue >= 1_000_000) return (numericValue / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M';
            if (numericValue >= 1_000) return (numericValue / 1_000).toFixed(0) + 'K';
            return numericValue.toString();
          }
        }
      },
    },
  };

  return (
    <div className="p-2 bg-white shadow-md rounded-md flex items-center justify-center">
      {xLabels.length === 0 ? (
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <svg
            className="w-5 h-5 animate-spin text-blue-600"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
            />
          </svg>
          Loading chart data...
        </div>
      ) : (
        <div style={{ height: "400px", width: "100%" }}>
          <Bar data={data} options={options} />
        </div>
      )}
    </div>
  );
};

export default HistogramChart;